import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, PauseCircle, AlertTriangle } from 'lucide-react';
import { campaignsAPI } from '../services/api.js';
import { formatDateTimeWithZone, formatPhone } from '../lib/format.js';

// SCHEDULED CALLS — "what is going out tomorrow morning?"
//
// ⚠️ THIS IS THE OTHER HALF, AND IT IS THE COMMON ONE. `UpcomingCalls` lists
// CALLBACKS (their own table, their own `matter`). Everything laid down with
// Add/schedule is `campaign_contacts.scheduledAt` — a different mechanism entirely,
// and the one that produces most of the volume. Brian scheduled 104 carriers and
// could not see them anywhere.
//
// NO BACKEND NEEDED. `GET /campaigns/:id/contacts` already returns scheduledAt on
// every row, and the campaign page has the whole list in memory — so the per-campaign
// view is free. The tenant-wide view fans out across campaigns, the same pattern the
// business search uses, and only on demand.
//
// ⛔ A SCHEDULED TIME IS NOT A PROMISE THAT THE CALL HAPPENS. dueContacts requires
// status='queued' AND slot un-fired AND the campaign ACTIVE. A paused campaign dials
// nothing, and a row can carry a time it will never act on. Both are stated, because
// a list that shows a time and stays silent about the campaign being paused is how
// three follow-ups sat invisible for a day.

/** Rows with a future time in either call slot, flattened to one entry per slot. */
function upcomingFrom(contacts, campaign) {
  const now = Date.now();
  const out = [];
  for (const c of contacts || []) {
    for (const [slot, at, fired] of [
      [1, c.scheduledAt, c.slot1Fired],
      [2, c.scheduledAt2, c.slot2Fired]
    ]) {
      if (!at) continue;
      const t = new Date(at).getTime();
      if (Number.isNaN(t) || t <= now) continue;
      out.push({
        key: `${c.id}:${slot}`,
        slot,
        at,
        company: c.company || c.name || formatPhone(c.phoneE164),
        phone: c.phoneE164,
        status: c.status,
        fired: !!fired,
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        campaignStatus: campaign?.status
      });
    }
  }
  return out.sort((a, b) => new Date(a.at) - new Date(b.at));
}

/**
 * @param {array}  contacts  already-loaded contacts (campaign page) — no fetch
 * @param {object} campaign  the campaign those contacts belong to
 * @param {bool}   tenantWide fan out across every campaign (Overview)
 */
export default function ScheduledCalls({ contacts, campaign, tenantWide = false, limit = 12, title = 'Scheduled calls' }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(tenantWide);
  const [failed, setFailed] = useState(false);

  // Campaign page: derive from what is already in memory. Zero requests.
  const local = useMemo(
    () => (tenantWide ? null : upcomingFrom(contacts, campaign)),
    [contacts, campaign, tenantWide]
  );

  // Overview: fan out. Lazy — only the campaigns that could actually dial.
  useEffect(() => {
    if (!tenantWide) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await campaignsAPI.list({ includeArchived: false });
        const camps = (r?.data || []).filter((c) => !c.archived);
        const all = [];
        const queue = [...camps];
        const worker = async () => {
          while (queue.length) {
            const c = queue.shift();
            try {
              const cr = await campaignsAPI.contacts(c.id, { limit: 5000 });
              all.push(...upcomingFrom(cr?.data || [], c));
            } catch { /* one campaign failing must not empty the list */ }
          }
        };
        await Promise.all([worker(), worker(), worker()]);
        if (!cancelled) setRows(all.sort((a, b) => new Date(a.at) - new Date(b.at)));
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantWide]);

  const data = tenantWide ? rows : local;
  if (loading) {
    return (
      <section className="card mb-6">
        <p className="text-sm text-stone-500">Looking for scheduled calls…</p>
      </section>
    );
  }
  if (failed || !data || data.length === 0) return null;

  const shown = data.slice(0, limit);
  const paused = data.filter((r) => r.campaignStatus && r.campaignStatus !== 'active');
  const notQueued = data.filter((r) => r.status && r.status !== 'queued');

  return (
    <section className="card mb-6">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">{title}</h2>
          <span className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px]">
            {data.length} upcoming
          </span>
        </div>
      </header>

      {/* The two ways a time on this list still does not become a call. */}
      {paused.length > 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <PauseCircle size={14} className="mt-0.5 shrink-0" />
          <span>
            {paused.length === data.length ? 'None of these will be dialled' : `${paused.length} of these won’t be dialled`} —
            {paused.length === 1 ? ' its campaign is' : ' their campaigns are'} paused. The dialer only runs active campaigns.
          </span>
        </p>
      )}
      {notQueued.length > 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {notQueued.length} {notQueued.length === 1 ? 'has a time but is' : 'have times but are'} not queued — a time alone
          doesn’t make a call; the contact has to be queued too.
        </p>
      )}

      <ul className="divide-y divide-stone-800">
        {shown.map((r) => (
          <li key={r.key} className="flex flex-wrap items-baseline justify-between gap-x-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="text-stone-200">{r.company}</span>
              {r.phone && <span className="text-stone-500"> · {formatPhone(r.phone)}</span>}
              {tenantWide && r.campaignName && (
                <Link to={`/campaigns/${r.campaignId}`} className="ml-2 text-xs text-stone-500 underline-offset-2 hover:text-alfred-gold hover:underline">
                  {r.campaignName}
                </Link>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-stone-400">
              <span>{formatDateTimeWithZone(r.at)}</span>
              {r.slot === 2 && <span className="text-stone-600">2nd call</span>}
              {r.status && r.status !== 'queued' && (
                <span className="badge bg-amber-600/20 text-amber-200 border border-amber-600/40 text-[10px]">{r.status}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
      {data.length > shown.length && (
        <p className="mt-2 text-xs text-stone-500">…and {data.length - shown.length} more.</p>
      )}
    </section>
  );
}
